import { useState, useRef, useEffect } from "react";

// ─── Melvo tokens ───────────────────────────────────────────────────────────
const C = {
  bg:      "#060608",
  surface: "#0d0d14",
  border:  "#ffffff10",
  borderHover: "#ffffff22",
  accent:  "#00e5a0",
  accentDim: "#00e5a018",
  accentBorder: "#00e5a035",
  accentGlow: "#00e5a040",
  text:    "#f2f2fa",
  muted:   "#8888aa",
  font:    "'Syne', sans-serif",
};

// ─── Sub-components ──────────────────────────────────────────────────────────

function ChoiceGrid({ options, value, onChange, columns = 2 }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
      gap: 10,
    }}>
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            style={{
              background: selected ? C.accentDim : C.surface,
              border: `1.5px solid ${selected ? C.accent : C.border}`,
              borderRadius: 12,
              padding: "14px 16px",
              cursor: "pointer",
              textAlign: "left",
              transition: "all 0.18s ease",
              transform: selected ? "scale(1.02)" : "scale(1)",
              boxShadow: selected ? `0 0 18px ${C.accentGlow}` : "none",
              position: "relative",
            }}
            onMouseEnter={e => {
              if (!selected) {
                e.currentTarget.style.borderColor = C.borderHover;
                e.currentTarget.style.background = "#111118";
              }
            }}
            onMouseLeave={e => {
              if (!selected) {
                e.currentTarget.style.borderColor = C.border;
                e.currentTarget.style.background = C.surface;
              }
            }}
          >
            {selected && (
              <span style={{
                position: "absolute", top: 10, right: 10,
                width: 16, height: 16, borderRadius: "50%",
                background: C.accent,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: C.bg, fontSize: 9, fontWeight: 800,
              }}>✓</span>
            )}
            <div style={{
              color: selected ? C.accent : C.text,
              fontSize: 14, fontWeight: 700,
              letterSpacing: "-0.01em",
              marginBottom: opt.sub ? 3 : 0,
              transition: "color 0.18s",
            }}>{opt.label}</div>
            {opt.sub && (
              <div style={{
                color: selected ? "#00e5a070" : C.muted,
                fontSize: 12, fontWeight: 400,
                transition: "color 0.18s",
              }}>{opt.sub}</div>
            )}
          </button>
        );
      })}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, multiline = false, rows = 2, autoFocus = false }) {
  const ref = useRef(null);
  useEffect(() => { if (autoFocus && ref.current) setTimeout(() => ref.current?.focus(), 60); }, [autoFocus]);

  const shared = {
    width: "100%",
    background: C.surface,
    border: `1.5px solid ${value?.trim() ? C.accentBorder : C.border}`,
    borderRadius: 12,
    padding: "14px 16px",
    color: C.text,
    fontSize: 15,
    fontWeight: 400,
    lineHeight: 1.6,
    fontFamily: C.font,
    transition: "border-color 0.18s, box-shadow 0.18s",
    boxShadow: value?.trim() ? `0 0 0 3px ${C.accentDim}` : "none",
    outline: "none",
  };

  const handlers = {
    onFocus: e => {
      e.target.style.borderColor = "#00e5a050";
      e.target.style.boxShadow = `0 0 0 3px ${C.accentDim}`;
    },
    onBlur: e => {
      e.target.style.borderColor = value?.trim() ? C.accentBorder : C.border;
      e.target.style.boxShadow = value?.trim() ? `0 0 0 3px ${C.accentDim}` : "none";
    },
  };

  return multiline ? (
    <textarea
      ref={ref}
      value={value || ""}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{ ...shared, resize: "none" }}
      {...handlers}
    />
  ) : (
    <input
      ref={ref}
      type="text"
      value={value || ""}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={shared}
      {...handlers}
    />
  );
}

// ─── Question definitions ────────────────────────────────────────────────────

const QUESTIONS = [
  {
    id: "business_info",
    num: "01",
    tag: "Your business",
    q: "What's your business called?",
    hint: "e.g. BrightSmile Dental, Mike's Auto Shop, Rosario's Italian Kitchen",
    type: "text",
    multiline: false,
  },
  {
    id: "business_type",
    num: "02",
    tag: "Your industry",
    q: "What type of business is this?",
    hint: null,
    type: "choice",
    columns: 2,
    options: [
      { value: "auto_repair",    label: "Auto Repair",      sub: "Shop, dealership, tires" },
      { value: "dental_medical", label: "Dental / Medical",  sub: "Clinic, practice, office" },
      { value: "restaurant",     label: "Restaurant",        sub: "Dine-in, takeout, café" },
      { value: "salon_spa",      label: "Salon / Spa",       sub: "Hair, nails, wellness" },
      { value: "home_services",  label: "Home Services",     sub: "Plumbing, HVAC, roofing" },
      { value: "other",          label: "Other",             sub: "Something else" },
    ],
  },
  {
    id: "city_state",
    num: "03",
    tag: "Your location",
    q: "What city are you in?",
    hint: "e.g. Minneapolis, MN",
    type: "text",
    multiline: false,
  },
  {
    id: "typical_visit",
    num: "04",
    tag: "The visit",
    q: "What does a typical customer visit look like?",
    hint: "e.g. dental cleaning appointment, oil change and tire rotation, dine-in dinner, roof estimate",
    type: "text",
    multiline: false,
  },
  {
    id: "business_description",
    num: "05",
    tag: "Your story",
    q: "Describe your business in your own words.",
    hint: "e.g. family-owned Italian restaurant for 30 years · on-call 24/7 plumbing · bowling alley with arcade and full bar",
    type: "text",
    multiline: true,
    rows: 3,
  },
  {
    id: "years_in_business",
    num: "06",
    tag: "Your history",
    q: "How long have you been in business?",
    hint: null,
    type: "choice",
    columns: 4,
    options: [
      { value: "under_1", label: "< 1 year",   sub: "Just started" },
      { value: "1_to_3",  label: "1–3 years",  sub: "Growing" },
      { value: "3_to_10", label: "3–10 years", sub: "Established" },
      { value: "10_plus", label: "10+ years",  sub: "Trusted" },
    ],
  },
  {
    id: "customer_base",
    num: "07",
    tag: "Your people",
    q: "Are your customers mostly regulars or new faces?",
    hint: null,
    type: "choice",
    columns: 3,
    options: [
      { value: "regulars", label: "Mostly regulars",      sub: "They know you well" },
      { value: "new",      label: "Mostly new customers", sub: "First-timers" },
      { value: "mixed",    label: "About 50/50",          sub: "All kinds" },
    ],
  },
  {
    id: "customer_feeling",
    num: "08",
    tag: "The feeling",
    q: "How do customers typically feel when they leave?",
    hint: null,
    type: "choice",
    columns: 2,
    options: [
      { value: "taken_care_of",  label: "Taken care of",  sub: "They felt looked after" },
      { value: "impressed",      label: "Impressed",       sub: "Exceeded expectations" },
      { value: "like_a_regular", label: "Like a regular",  sub: "Familiar and welcome" },
      { value: "relieved",       label: "Relieved",        sub: "Problem solved" },
    ],
  },
  {
    id: "tone",
    num: "09",
    tag: "Your voice",
    q: "How would you describe your tone with customers?",
    hint: null,
    type: "choice",
    columns: 2,
    options: [
      { value: "casual",       label: "Casual & friendly",     sub: "Relaxed, like a neighbor" },
      { value: "warm",         label: "Warm & personal",       sub: "Caring, close-knit" },
      { value: "professional", label: "Professional",          sub: "Crisp and trusted" },
      { value: "direct",       label: "Straight to the point", sub: "No fluff" },
    ],
  },
  {
    id: "avoid_phrases",
    num: "10",
    tag: "Hard no's",
    q: "Is there anything you'd never want a message to say?",
    hint: "e.g. nothing pushy, no emojis, never mention competitors, don't make it sound like a form letter",
    type: "text",
    multiline: true,
    rows: 2,
    optional: true,
  },
  {
    id: "brand_feeling",
    num: "11",
    tag: "The goal",
    q: "What do you want every customer to feel after hearing from you?",
    hint: "e.g. appreciated, like we genuinely noticed them, like they made the right call",
    type: "text",
    multiline: false,
  },
];

// ─── Main component ──────────────────────────────────────────────────────────

/**
 * QuestionnaireStep
 * Props:
 *   data     — current answers object (keyed by question id)
 *   onChange — (updatedData) => void   called on every answer change
 *   onComplete — () => void   called when all required questions answered + user submits
 */
export default function QuestionnaireStep({ data = {}, onChange, onComplete }) {
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(true);
  const [dir, setDir] = useState("fwd");

  const q = QUESTIONS[current];
  const isLast = current === QUESTIONS.length - 1;
  const answer = data[q.id] || "";
  const hasAnswer = q.optional ? true : (q.type === "choice" ? !!answer : answer.trim().length > 0);

  const setAnswer = (val) => onChange({ ...data, [q.id]: val });

  const slide = (direction, fn) => {
    setDir(direction);
    setVisible(false);
    setTimeout(() => { fn(); setVisible(true); }, 260);
  };

  const next = () => {
    if (!hasAnswer) return;
    if (isLast) { onComplete?.(); return; }
    slide("fwd", () => setCurrent(c => c + 1));
  };

  const back = () => {
    if (current === 0) return;
    slide("back", () => setCurrent(c => c - 1));
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey && q.type === "choice") { e.preventDefault(); next(); }
  };

  const progress = ((current + 1) / QUESTIONS.length) * 100;

  return (
    <div style={{ width: "100%", fontFamily: C.font }} onKeyDown={handleKey}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        textarea::placeholder, input::placeholder { color: ${C.muted}; opacity: 1; }
        textarea { font-family: ${C.font} !important; }
        input    { font-family: ${C.font} !important; }
        ::selection { background: #00e5a022; color: #00e5a0; }
      `}</style>

      {/* Progress bar */}
      <div style={{ height: 3, background: "#ffffff08", borderRadius: 2, marginBottom: 32, overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${progress}%`,
          background: `linear-gradient(90deg, #00b87d, ${C.accent})`,
          transition: "width 0.45s cubic-bezier(0.4,0,0.2,1)",
          boxShadow: `0 0 10px ${C.accentGlow}`,
          borderRadius: 2,
        }} />
      </div>

      {/* Step indicator dots */}
      <div style={{ display: "flex", gap: 5, marginBottom: 28 }}>
        {QUESTIONS.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => { if (i < current) slide("back", () => setCurrent(i)); }}
            style={{
              width: i === current ? 20 : 6,
              height: 6,
              borderRadius: 3,
              border: "none",
              padding: 0,
              cursor: i < current ? "pointer" : "default",
              background: i === current ? C.accent : i < current ? "#00e5a055" : "#ffffff12",
              transition: "all 0.3s ease",
            }}
          />
        ))}
        <span style={{
          marginLeft: "auto",
          color: C.muted, fontSize: 12, fontWeight: 500,
          alignSelf: "center",
        }}>
          {current + 1} / {QUESTIONS.length}
        </span>
      </div>

      {/* Question card */}
      <div style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : dir === "fwd" ? "translateY(16px)" : "translateY(-16px)",
        transition: "opacity 0.25s ease, transform 0.25s ease",
      }}>
        {/* Tag */}
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 7,
          padding: "5px 11px", borderRadius: 20,
          background: C.accentDim, border: `1px solid ${C.accentBorder}`,
          marginBottom: 16,
        }}>
          <span style={{ color: C.accent, fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase" }}>
            {q.num}
          </span>
          <span style={{ width: 1, height: 9, background: C.accentBorder }} />
          <span style={{ color: C.accent, fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            {q.tag}
          </span>
        </div>

        {/* Question text */}
        <h2 style={{
          color: C.text,
          fontSize: "clamp(18px, 3.5vw, 24px)",
          fontWeight: 700,
          lineHeight: 1.3,
          letterSpacing: "-0.025em",
          marginBottom: q.optional ? 6 : 20,
        }}>
          {q.q}
        </h2>

        {q.optional && (
          <p style={{ color: C.muted, fontSize: 13, marginBottom: 20 }}>
            Optional — skip if nothing comes to mind
          </p>
        )}

        {/* Input */}
        {q.type === "text" && (
          <TextInput
            value={answer}
            onChange={setAnswer}
            placeholder={q.hint}
            multiline={q.multiline}
            rows={q.rows}
            autoFocus={true}
          />
        )}

        {q.type === "choice" && (
          <ChoiceGrid
            options={q.options}
            value={answer}
            onChange={(val) => {
              setAnswer(val);
            }}
            columns={q.columns}
          />
        )}

        {/* Nav */}
        <div style={{ display: "flex", gap: 10, marginTop: 28, alignItems: "center" }}>
          <button
            type="button"
            onClick={back}
            disabled={current === 0}
            style={{
              display: "flex", alignItems: "center", gap: 7,
              background: "transparent",
              border: `1.5px solid ${current === 0 ? "#ffffff08" : C.border}`,
              borderRadius: 10, padding: "10px 18px",
              color: current === 0 ? "#ffffff18" : C.muted,
              fontSize: 13, fontWeight: 600,
              cursor: current === 0 ? "default" : "pointer",
              fontFamily: C.font,
              transition: "all 0.18s",
            }}
            onMouseEnter={e => { if (current > 0) { e.currentTarget.style.borderColor = "#ffffff28"; e.currentTarget.style.color = C.text; }}}
            onMouseLeave={e => { e.currentTarget.style.borderColor = current === 0 ? "#ffffff08" : C.border; e.currentTarget.style.color = current === 0 ? "#ffffff18" : C.muted; }}
          >
            ← Back
          </button>

          <button
            type="button"
            onClick={next}
            disabled={!hasAnswer}
            style={{
              flex: 1,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              background: hasAnswer ? `linear-gradient(135deg, #00e5a0, #00c48a)` : "#ffffff08",
              border: "none",
              borderRadius: 10, padding: "11px 24px",
              color: hasAnswer ? C.bg : "#ffffff20",
              fontSize: 14, fontWeight: 700,
              cursor: hasAnswer ? "pointer" : "default",
              fontFamily: C.font,
              letterSpacing: "0.01em",
              transition: "all 0.2s",
              boxShadow: hasAnswer ? `0 4px 18px ${C.accentGlow}` : "none",
            }}
            onMouseEnter={e => { if (hasAnswer) { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = `0 6px 24px #00e5a055`; }}}
            onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = hasAnswer ? `0 4px 18px ${C.accentGlow}` : "none"; }}
          >
            {isLast ? "✦ Generate my templates" : "Continue →"}
          </button>
        </div>

        {/* Enter hint for choice questions */}
        {q.type === "choice" && answer && !isLast && (
          <p style={{ textAlign: "center", color: C.muted, fontSize: 12, marginTop: 12 }}>
            Press <kbd style={{ background: "#ffffff08", border: `1px solid ${C.border}`, borderRadius: 4, padding: "1px 5px", fontSize: 11 }}>Enter</kbd> to continue
          </p>
        )}
      </div>
    </div>
  );
}
